import { useEffect, useState } from "react";
import {
  CalendarDays, Check, Clock, Eye, Link2, MapPin, Phone, Trash2, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const STATUS_META = {
  pending: { label: "En attente", cls: "bg-amber-50 text-amber-600" },
  published: { label: "Publié", cls: "bg-emerald-50 text-emerald-600" },
  rejected: { label: "Refusé", cls: "bg-red-50 text-red-500" },
  draft: { label: "Brouillon", cls: "bg-slate-100 text-slate-500" },
};

const SOURCE_META = {
  user: { label: "Utilisateur", cls: "bg-blue-50 text-[#2563EB]" },
  pro: { label: "Établissement Pro", cls: "bg-violet-50 text-violet-600" },
  admin: { label: "Admin", cls: "bg-slate-100 text-slate-500" },
};

export const statusMetaOf = (e) => STATUS_META[e.status] || STATUS_META.published;
const sourceMetaOf = (e) => SOURCE_META[e.source] || SOURCE_META.admin;

const chipCls = (active) =>
  `flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${
    active ? "border-[#2563EB] bg-[#2563EB] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-[#2563EB]"
  }`;

// Section admin « Événements » : modération des soumissions (Approuver/Refuser) + identification de la source.
export const AdminEvents = () => {
  const { t, lang, fmtDate } = useLanguage();
  const [events, setEvents] = useState(null);
  const [statusFilter, setStatusFilter] = useState("tous");
  const [sourceFilter, setSourceFilter] = useState("tous");
  const [detail, setDetail] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));

  useEffect(() => {
    load();
  }, []);

  const moderate = async (slug, status, rejectionReason = "") => {
    setBusy(true);
    try {
      await api.patch(`/admin/events/${slug}/moderation`, { status, reason: rejectionReason });
      toast.success(
        status === "published"
          ? t("Événement approuvé — il est maintenant visible publiquement.")
          : status === "rejected"
            ? t("Événement refusé — visible uniquement par son auteur.")
            : t("Événement repassé en attente.")
      );
      setDetail(null);
      setRejecting(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(t(formatApiError(e.response?.data?.detail)));
    } finally {
      setBusy(false);
    }
  };

  const removeEvent = async (slug) => {
    if (!window.confirm(t("Supprimer cet événement et ses réservations ?"))) return;
    try {
      await api.delete(`/admin/events/${slug}`);
      toast.success(t("Événement supprimé"));
      setDetail(null);
      load();
    } catch (e) {
      toast.error(t(formatApiError(e.response?.data?.detail)));
    }
  };

  const fmtSubmitted = (iso) => {
    if (!iso) return "—";
    return new Intl.DateTimeFormat(lang === "en" ? "en-CA" : "fr-CA", { day: "numeric", month: "short", year: "numeric" }).format(new Date(iso));
  };

  const filtered = (events || []).filter((e) => {
    const st = e.status || "published";
    if (statusFilter !== "tous" && st !== statusFilter) return false;
    if (sourceFilter !== "tous" && (e.source || "admin") !== sourceFilter) return false;
    return true;
  });

  const countOf = (st) => (events || []).filter((e) => (e.status || "published") === st).length;

  const actionBtn =
    "flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition disabled:opacity-50";

  return (
    <div className="mt-4" data-testid="admin-events">
      {/* FILTRES */}
      <div className="flex flex-wrap items-center gap-2" data-testid="admin-events-status-filters">
        {[
          { key: "tous", label: t("Tous"), count: (events || []).length },
          { key: "pending", label: t("En attente"), count: countOf("pending") },
          { key: "published", label: t("Publiés"), count: countOf("published") },
          { key: "rejected", label: t("Refusés"), count: countOf("rejected") },
        ].map((f) => (
          <button key={f.key} type="button" data-testid={`filter-status-${f.key}`} onClick={() => setStatusFilter(f.key)} className={chipCls(statusFilter === f.key)}>
            {f.label}
            <span className={`rounded-full px-1.5 text-[10px] font-bold ${statusFilter === f.key ? "bg-white/20" : "bg-slate-100"}`}>{f.count}</span>
          </button>
        ))}
        <span className="mx-1 hidden h-5 w-px bg-slate-200 sm:block" />
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t("Source")}</span>
        {[
          { key: "tous", label: t("Tous") },
          { key: "user", label: t("Utilisateur") },
          { key: "pro", label: t("Établissement Pro") },
          { key: "admin", label: t("Admin") },
        ].map((f) => (
          <button key={f.key} type="button" data-testid={`filter-source-${f.key}`} onClick={() => setSourceFilter(f.key)} className={chipCls(sourceFilter === f.key)}>
            {f.label}
          </button>
        ))}
      </div>

      {/* LISTE */}
      {events === null ? (
        <div className="mt-4 space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-white" />)}</div>
      ) : filtered.length === 0 ? (
        <p data-testid="admin-events-empty" className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-400">
          {t("Aucun événement ne correspond à ces filtres.")}
        </p>
      ) : (
        <div className="mt-4 space-y-2">
          {filtered.map((e) => {
            const st = statusMetaOf(e);
            const src = sourceMetaOf(e);
            return (
              <div key={e.slug} data-testid={`admin-event-${e.slug}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                <img src={e.image} alt={e.title} className="h-11 w-11 shrink-0 rounded-xl object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
                    <span className="truncate">{e.title}</span>
                    <span data-testid={`admin-event-status-${e.slug}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${st.cls}`}>{t(st.label)}</span>
                  </p>
                  <p className="truncate text-xs text-slate-400">
                    {e.venue} • {fmtDate(e.date)} • {e.price > 0 ? `${e.price} $` : t("Gratuit")}
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-400">
                    <span data-testid={`admin-event-source-${e.slug}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${src.cls}`}>{t(src.label)}</span>
                    <span>{e.owner_name || (e.source === "admin" ? t("Équipe GoSpot") : "—")}</span>
                    {e.created_at && <span>• {t("Soumis le")} {fmtSubmitted(e.created_at)}</span>}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button type="button" data-testid={`admin-view-event-${e.slug}`} onClick={() => setDetail(e)} aria-label={t("Voir")} title={t("Voir")} className={`${actionBtn} hover:border-blue-300 hover:text-[#2563EB]`}>
                    <Eye className="h-4 w-4" />
                  </button>
                  {(e.status || "published") !== "published" && (
                    <button type="button" data-testid={`admin-approve-event-${e.slug}`} onClick={() => moderate(e.slug, "published")} disabled={busy} aria-label={t("Approuver")} title={t("Approuver")} className={`${actionBtn} hover:border-emerald-300 hover:text-emerald-600`}>
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                  {(e.status || "published") !== "rejected" && (
                    <button type="button" data-testid={`admin-reject-event-${e.slug}`} onClick={() => { setRejecting(e); setReason(""); }} disabled={busy} aria-label={t("Refuser")} title={t("Refuser")} className={`${actionBtn} hover:border-amber-300 hover:text-amber-500`}>
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  <button type="button" data-testid={`admin-delete-event-${e.slug}`} onClick={() => removeEvent(e.slug)} aria-label={t("Supprimer")} title={t("Supprimer")} className={`${actionBtn} hover:border-red-300 hover:text-red-500`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODALE DÉTAIL */}
      {detail && (
        <div className="fixed inset-0 z-[80] overflow-y-auto bg-slate-900/50 p-4" data-testid="admin-event-detail-modal">
          <div className="mx-auto my-6 w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <h3 className="text-lg font-extrabold tracking-tight text-slate-900">{t("Détails de l'événement")}</h3>
              <button type="button" data-testid="admin-event-detail-close" onClick={() => setDetail(null)} aria-label={t("Fermer")} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200">
                <X className="h-4 w-4" />
              </button>
            </div>
            <img src={detail.image} alt={detail.title} className="mt-4 h-40 w-full rounded-2xl object-cover" />
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${statusMetaOf(detail).cls}`}>{t(statusMetaOf(detail).label)}</span>
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${sourceMetaOf(detail).cls}`}>{t(sourceMetaOf(detail).label)}</span>
            </div>
            <h4 className="mt-3 text-base font-bold text-slate-900">{detail.title}</h4>
            <div className="mt-2 space-y-1.5 text-sm text-slate-600">
              <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-slate-400" />{fmtDate(detail.date)}</p>
              <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-slate-400" />{detail.time_label}{detail.end_time ? ` – ${detail.end_time}` : ""}</p>
              <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" />{detail.venue}{detail.city ? `, ${detail.city}` : detail.neighborhood ? `, ${detail.neighborhood}` : ""}</p>
              <p className="flex items-center gap-2"><span className="w-4 text-center text-xs font-bold text-slate-400">$</span>{detail.price > 0 ? `${detail.price} $` : t("Gratuit")} • {t(detail.category_label || "")}</p>
              {detail.ticket_url && (
                <p className="flex items-center gap-2">
                  <Link2 className="h-4 w-4 text-slate-400" />
                  <a href={detail.ticket_url} target="_blank" rel="noreferrer" data-testid="admin-event-ticket-link" className="truncate font-semibold text-[#2563EB] hover:underline">{t("Lien de réservation")}</a>
                </p>
              )}
              {detail.contact && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" />{detail.contact}</p>}
            </div>
            <div className="mt-4 rounded-2xl bg-slate-50 p-4" data-testid="admin-event-organizer">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t("Organisateur")}</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{detail.owner_name || t("Équipe GoSpot")}</p>
              {detail.owner_email && <p className="text-xs text-slate-500">{detail.owner_email}</p>}
              <p className="mt-1 text-[11px] text-slate-400">{t("Soumis le")} {fmtSubmitted(detail.created_at)}</p>
            </div>
            {detail.description && <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-slate-600">{detail.description}</p>}
            {detail.status === "rejected" && detail.rejection_reason && (
              <p className="mt-4 rounded-2xl bg-red-50 px-4 py-2.5 text-xs text-red-600">
                <strong>{t("Raison du refus")} :</strong> {detail.rejection_reason}
              </p>
            )}
            <div className="mt-5 flex flex-wrap gap-2.5">
              {(detail.status || "published") !== "published" && (
                <button type="button" data-testid="admin-event-detail-approve" onClick={() => moderate(detail.slug, "published")} disabled={busy} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-emerald-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-600 disabled:opacity-60">
                  <Check className="h-4 w-4" />
                  {t("Approuver")}
                </button>
              )}
              {(detail.status || "published") !== "rejected" && (
                <button type="button" data-testid="admin-event-detail-reject" onClick={() => { setRejecting(detail); setReason(""); }} disabled={busy} className="flex flex-1 items-center justify-center gap-2 rounded-full border border-amber-200 px-5 py-3 text-sm font-semibold text-amber-600 transition hover:bg-amber-50 disabled:opacity-60">
                  <X className="h-4 w-4" />
                  {t("Refuser")}
                </button>
              )}
              <button type="button" data-testid="admin-event-detail-delete" onClick={() => removeEvent(detail.slug)} className="flex items-center justify-center gap-2 rounded-full border border-red-200 px-5 py-3 text-sm font-semibold text-red-600 transition hover:bg-red-50">
                <Trash2 className="h-4 w-4" />
                {t("Supprimer")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODALE REFUS */}
      {rejecting && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/50 p-4" data-testid="admin-event-reject-modal">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
            <h3 className="text-lg font-extrabold tracking-tight text-slate-900">{t("Refuser l'événement")}</h3>
            <p className="mt-1 text-sm text-slate-500">« {rejecting.title} »</p>
            <label className="mb-1.5 mt-4 block text-sm font-semibold text-slate-700">{t("Raison du refus (optionnelle)")}</label>
            <textarea
              data-testid="admin-reject-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("Ex. : informations insuffisantes, contenu inapproprié…")}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100"
            />
            <p className="mt-2 text-[11px] text-slate-400">{t("L'événement refusé restera visible uniquement dans « Mes événements » de son auteur.")}</p>
            <div className="mt-5 flex gap-2.5">
              <button type="button" data-testid="admin-reject-confirm" onClick={() => moderate(rejecting.slug, "rejected", reason)} disabled={busy} className="flex-1 rounded-full bg-red-500 py-3 text-sm font-semibold text-white transition hover:bg-red-600 disabled:opacity-60">
                {t("Confirmer le refus")}
              </button>
              <button type="button" data-testid="admin-reject-cancel" onClick={() => { setRejecting(null); setReason(""); }} className="rounded-full border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-600 transition hover:border-blue-300 hover:text-[#2563EB]">
                {t("Annuler")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
